/**
 * WebMCP integration — exposes site capabilities to in-browser AI agents
 * via the proposed navigator.modelContext API.
 *
 * Spec: https://webmachinelearning.github.io/webmcp/
 *
 * Tools are intentionally minimal and read-only-ish: the agent can look up
 * FAQ answers, list our programs, and prepare a contact-form draft. No
 * destructive actions are exposed.
 */
import prerenderedFaqs from "@/data/faqs-prerender.json";

interface FaqEntry {
  question: string;
  answer_intro: string | null;
  answer_bullets: string[];
  category: string;
}

interface ModelContextTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<unknown> | unknown;
}

interface ModelContext {
  provideContext: (ctx: { tools?: ModelContextTool[] }) => void | Promise<void>;
}

declare global {
  interface Navigator {
    modelContext?: ModelContext;
  }
}

function faqAnswerText(faq: FaqEntry): string {
  const parts: string[] = [];
  if (faq.answer_intro) parts.push(faq.answer_intro);
  if (faq.answer_bullets?.length) parts.push(faq.answer_bullets.map((b) => `• ${b}`).join("\n"));
  return parts.join("\n\n");
}

const PROGRAMS = [
  {
    id: "transition-90-day",
    name: "90-Day Transition Program",
    cost: "Free",
    audience: "Unhoused adults willing to commit to structured case management",
    status: "Not yet operational — fundraising phase",
  },
  {
    id: "independent-membership",
    name: "Independent Membership",
    cost: "$750/month",
    audience: "Graduates of the 90-Day Program in good standing",
    status: "Not yet operational — fundraising phase",
  },
  {
    id: "direct-entry",
    name: "Direct Entry",
    cost: "$750/month",
    audience: "Employed or fixed-income adults priced out of housing",
    status: "Not yet operational — fundraising phase",
  },
];

export function registerWebMCP() {
  if (typeof navigator === "undefined") return;
  const mc = navigator.modelContext;
  if (!mc?.provideContext) return;

  const faqs = prerenderedFaqs as FaqEntry[];

  const tools: ModelContextTool[] = [
    {
      name: "search_faq",
      description:
        "Search Abba's House Communities FAQ for answers about programs, eligibility, costs, locations, donations, and how to apply. Returns matching question/answer pairs.",
      inputSchema: {
        type: "object",
        properties: {
          query: { type: "string", description: "Search keywords or a question." },
          limit: { type: "integer", minimum: 1, maximum: 10, default: 3 },
        },
        required: ["query"],
      },
      execute: ({ query, limit }) => {
        const q = String(query ?? "").toLowerCase().trim();
        const max = Number(limit ?? 3);
        if (!q) return [];
        const scored = faqs
          .map((f) => {
            const hay = `${f.question}\n${faqAnswerText(f)}\n${f.category}`.toLowerCase();
            const score = q.split(/\s+/).reduce((s, term) => s + (hay.includes(term) ? 1 : 0), 0);
            return { f, score };
          })
          .filter((x) => x.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, max)
          .map(({ f }) => ({
            question: f.question,
            answer: faqAnswerText(f),
            category: f.category,
            source: "https://abbashouse.org/faq",
          }));
        return scored;
      },
    },
    {
      name: "list_programs",
      description:
        "List Abba's House Communities housing programs with cost, audience, and operational status.",
      inputSchema: { type: "object", properties: {} },
      execute: () => PROGRAMS,
    },
    {
      name: "get_donation_info",
      description:
        "Get how to donate to Abba's House Communities (501(c)(3), EIN 41-4644298). Returns donation URL and methods.",
      inputSchema: { type: "object", properties: {} },
      execute: () => ({
        organization: "Abba's House Communities Inc.",
        ein: "41-4644298",
        primary_url: "https://give.abbashouse.org",
        methods: [
          "One-time or recurring gift at https://give.abbashouse.org",
          "Donor-Advised Funds via Fidelity Charitable",
          "Planned giving / major gifts: hello@abbashouse.org",
        ],
        tax_status: "Tax-deductible to the fullest extent allowed by law",
      }),
    },
    {
      name: "open_contact_form",
      description:
        "Navigate the user to the contact form, optionally pre-filling category, name, email, and message via URL params. Use this instead of attempting to submit on the user's behalf.",
      inputSchema: {
        type: "object",
        properties: {
          category: {
            type: "string",
            enum: ["General Inquiry", "Partnership", "Volunteer", "Media", "Prayer Request"],
          },
          name: { type: "string" },
          email: { type: "string", format: "email" },
          message: { type: "string", maxLength: 5000 },
        },
      },
      execute: (args) => {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(args ?? {})) {
          if (typeof v === "string" && v.trim()) params.set(k, v);
        }
        const url = `/contact${params.toString() ? `?${params}` : ""}`;
        if (typeof window !== "undefined") window.location.assign(url);
        return { navigated_to: url };
      },
    },
  ];

  try {
    void mc.provideContext({ tools });
  } catch (err) {
    console.warn("WebMCP provideContext failed:", err);
  }
}
